import React, { useState, useMemo } from "react";
import {
  Box,
  Button,
  Popover,
  Typography,
  useTheme,
  Icon,
  alpha,
  Divider,
  Stack,
  Chip,
  TextField,
} from "@mui/material";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterMoment } from "@mui/x-date-pickers/AdapterMoment";
import moment, { Moment } from "moment";
import { motion, AnimatePresence } from "framer-motion";

interface DateRangePickerProps {
  startDate: Date;
  endDate: Date;
  onChange: (range: { start: Date; end: Date }) => void;
}

interface PresetRange {
  label: string;
  value: string;
  range: () => { start: Date; end: Date };
}

export const DateRangePicker: React.FC<DateRangePickerProps> = ({
  startDate,
  endDate,
  onChange,
}) => {
  const theme = useTheme();
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [tempStartDate, setTempStartDate] = useState<Date>(startDate);
  const [tempEndDate, setTempEndDate] = useState<Date>(endDate);
  const [selectedPreset, setSelectedPreset] = useState<string>("");

  const presetRanges: PresetRange[] = useMemo(
    () => [
      {
        label: "Today",
        value: "today",
        range: () => ({
          start: moment().startOf("day").toDate(),
          end: moment().endOf("day").toDate(),
        }),
      },
      {
        label: "Yesterday",
        value: "yesterday",
        range: () => ({
          start: moment().subtract(1, "day").startOf("day").toDate(),
          end: moment().subtract(1, "day").endOf("day").toDate(),
        }),
      },
      {
        label: "Last 7 Days",
        value: "7days",
        range: () => ({
          start: moment().subtract(6, "days").startOf("day").toDate(),
          end: moment().endOf("day").toDate(),
        }),
      },
      {
        label: "Last 30 Days",
        value: "30days",
        range: () => ({
          start: moment().subtract(29, "days").startOf("day").toDate(),
          end: moment().endOf("day").toDate(),
        }),
      },
      {
        label: "This Month",
        value: "thisMonth",
        range: () => ({
          start: moment().startOf("month").toDate(),
          end: moment().endOf("month").toDate(),
        }),
      },
      {
        label: "Last Month",
        value: "lastMonth",
        range: () => ({
          start: moment().subtract(1, "month").startOf("month").toDate(),
          end: moment().subtract(1, "month").endOf("month").toDate(),
        }),
      },
    ],
    []
  );

  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
    setSelectedPreset("");
  };

  const handleApply = () => {
    onChange({ start: tempStartDate, end: tempEndDate });
    handleClose();
  };

  const handlePresetSelect = (preset: PresetRange) => {
    const { start, end } = preset.range();
    setTempStartDate(start);
    setTempEndDate(end);
    setSelectedPreset(preset.value);
  };

  const formatDateRange = () => {
    const start = moment(startDate);
    const end = moment(endDate);

    if (start.isSame(end, "day")) {
      return start.format("MMM D, YYYY");
    }

    if (start.year() === end.year()) {
      if (start.month() === end.month()) {
        return `${start.format("MMM D")} - ${end.format("D, YYYY")}`;
      }
      return `${start.format("MMM D")} - ${end.format("MMM D, YYYY")}`;
    }

    return `${start.format("MMM D, YYYY")} - ${end.format("MMM D, YYYY")}`;
  };

  const open = Boolean(anchorEl);

  return (
    <>
      <Button
        variant="outlined"
        onClick={handleClick}
        startIcon={<Icon>calendar_today</Icon>}
        sx={{
          color: "#0891B2",
          borderColor: alpha("#0891B2", 0.2),
          bgcolor: alpha("#0891B2", 0.04),
          "&:hover": {
            borderColor: alpha("#0891B2", 0.3),
            bgcolor: alpha("#0891B2", 0.08),
          },
          "& .MuiIcon-root": {
            color: "#0891B2",
          },
        }}
      >
        {formatDateRange()}
      </Button>

      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "left",
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "left",
        }}
        PaperProps={{
          sx: {
            mt: 1,
            p: 3,
            width: 360,
            border: `1px solid ${theme.palette.divider}`,
            borderRadius: 2,
            boxShadow: `0 4px 24px ${alpha(theme.palette.common.black, 0.08)}`,
          },
        }}
        sx={{
          "& .MuiPickersDay-root.Mui-selected": {
            bgcolor: "#0891B2 !important",
          },
          "& .MuiPickersDay-root.Mui-selected:hover": {
            bgcolor: alpha("#0891B2", 0.9),
          },
          "& .MuiPickersDay-root:hover": {
            bgcolor: alpha("#0891B2", 0.1),
          },
          "& .MuiPickersDay-today": {
            borderColor: "#0891B2",
          },
          "& .MuiButton-text": {
            color: "#0891B2",
          },
          "& .MuiPickersCalendarHeader-label": {
            color: theme.palette.text.primary,
          },
          "& .MuiPickersArrowSwitcher-button": {
            color: "#0891B2",
          },
        }}
      >
        <AnimatePresence>
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 10 }}
            transition={{ duration: 0.2 }}
          >
            <Box sx={{ mb: 3 }}>
              <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 600 }}>
                Select Date Range
              </Typography>
              <Stack
                direction="row"
                spacing={1}
                flexWrap="wrap"
                useFlexGap
                sx={{ mb: 2 }}
              >
                {presetRanges.map((preset) => (
                  <Chip
                    key={preset.value}
                    label={preset.label}
                    onClick={() => handlePresetSelect(preset)}
                    variant={
                      selectedPreset === preset.value ? "filled" : "outlined"
                    }
                    color={
                      selectedPreset === preset.value ? "primary" : "default"
                    }
                    size="small"
                    sx={{
                      borderRadius: 1.5,
                      "&:hover": {
                        bgcolor:
                          selectedPreset === preset.value
                            ? theme.palette.primary.main
                            : alpha(theme.palette.primary.main, 0.04),
                      },
                    }}
                  />
                ))}
              </Stack>
              <Divider sx={{ my: 2 }} />
            </Box>

            <LocalizationProvider dateAdapter={AdapterMoment}>
              <Stack spacing={2}>
                <DatePicker
                  label="Start Date"
                  value={moment(tempStartDate)}
                  onChange={(date) => {
                    if (moment.isMoment(date)) {
                      setTempStartDate(date.toDate());
                    }
                  }}
                  maxDate={moment(tempEndDate)}
                  renderInput={(params) => (
                    <TextField {...params} size="small" fullWidth />
                  )}
                />
                <DatePicker
                  label="End Date"
                  value={moment(tempEndDate)}
                  onChange={(date) => {
                    if (moment.isMoment(date)) {
                      setTempEndDate(date.toDate());
                    }
                  }}
                  minDate={moment(tempStartDate)}
                  maxDate={moment()}
                  renderInput={(params) => (
                    <TextField {...params} size="small" fullWidth />
                  )}
                />
              </Stack>
            </LocalizationProvider>

            <Box sx={{ display: "flex", gap: 1, mt: 3 }}>
              <Button
                size="small"
                onClick={handleClose}
                variant="outlined"
                sx={{
                  flex: 1,
                  borderRadius: 2,
                  textTransform: "none",
                  borderColor: theme.palette.divider,
                  color: "text.secondary",
                  "&:hover": {
                    borderColor: theme.palette.text.secondary,
                    bgcolor: "transparent",
                  },
                }}
              >
                Cancel
              </Button>
              <Button
                size="small"
                onClick={handleApply}
                variant="contained"
                sx={{
                  flex: 1,
                  borderRadius: 2,
                  textTransform: "none",
                  boxShadow: "none",
                  "&:hover": {
                    boxShadow: "none",
                  },
                }}
              >
                Apply Range
              </Button>
            </Box>
          </motion.div>
        </AnimatePresence>
      </Popover>
    </>
  );
};
